import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import BackButton from '../../components/BackButton';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import InfoIcon from '@mui/icons-material/Info';

const ConsolidatedAcemoneySettlements = () => {
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalRecords: 0,
    limit: 25
  });

  // Filters
  const [status, setStatus] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Settlement details dialog
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchSettlements();
  }, [pagination.currentPage, status, paymentMode, searchQuery]);

  const validateDates = () => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    const diffInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    if (diffInDays > 90) {
      setDialogMessage('Date range cannot be more than 90 days.');
      setOpenDialog(true);
      return false;
    }
    return true;
  };

  const fetchSettlements = async () => {
    if (!validateDates()) return;

    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const userId = token ? jwtDecode(token).userId : null;
      const params = {
        page: pagination.currentPage,
        limit: pagination.limit,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        ...(status && { status }),
        ...(paymentMode && { paymentMode }),
        ...(searchQuery && { search: searchQuery })
      };

      const response = await axios.get(
        `${API_BASE_URL}/acemoney/reports/settlements/${userId}`,
        { params }
      );

      if (response.data.success) {
        setSettlements(response.data.settlements);
        setStatistics(response.data.statistics);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching settlements:', error);
      setDialogMessage(
        'Failed to fetch settlements. Please try again.'
      );
      setOpenDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage + 1 }));
  };

  const handleExport = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const userId = token ? jwtDecode(token).userId : null;
      const params = {
        format: 'csv',
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        ...(status && { status })
      };

      const response = await axios.get(
        `${API_BASE_URL}/acemoney/reports/settlements/export/${userId}`,
        { params }
      );

      setDialogMessage(`Export ready: ${response.data.count} settlements`);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error exporting:', error);
      setDialogMessage('Failed to export settlements');
      setOpenDialog(true);
    }
  };

  const handleViewDetails = async (settlementRefNo) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/acemoney/reports/settlements/details/${settlementRefNo}`
      );
      if (response.data.success) {
        setSelectedSettlement(response.data.settlement);
        setDetailsDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching settlement details:', error);
      setDialogMessage('Failed to fetch settlement details');
      setOpenDialog(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'failed':
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: 'slNo',
      headerName: 'Sl No',
      width: 70,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1 + (pagination.currentPage - 1) * pagination.limit
    },
    {
      field: 'settlementRefNo',
      headerName: 'Settlement Ref No',
      width: 200,
      renderCell: (params) => (
        <Tooltip title="Click to view details">
          <Typography
            variant="body2"
            sx={{ cursor: 'pointer', color: 'primary.main' }}
            onClick={() => handleViewDetails(params.value)}
          >
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'shopName',
      headerName: 'Shop',
      width: 150,
      valueGetter: (params) => params.row.shopId?.shopname || params.row.shopName || 'N/A'
    },
    {
      field: 'shopMobile',
      headerName: 'Shop Mobile',
      width: 130,
      valueGetter: (params) => params.row.shopId?.phoneNumber || params.row.shopMobile || 'N/A'
    },
    {
      field: 'agentcode',
      headerName: 'Agent Code',
      width: 150
    },
    {
      field: 'settlementAmount',
      headerName: 'Settlement Amount',
      width: 150,
      renderCell: (params) => `₹${params.value?.toFixed(2)}`
    },
    {
      field: 'commission',
      headerName: 'Commission',
      width: 120,
      renderCell: (params) => `₹${params.value?.toFixed(2)}`
    },
    {
      field: 'registeredCount',
      headerName: 'Registered',
      width: 110,
      align: 'center'
    },
    {
      field: 'notRegisteredCount',
      headerName: 'Not Registered',
      width: 130,
      align: 'center'
    },
    {
      field: 'settlementStatus',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value?.toUpperCase()}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'paymentMode',
      headerName: 'Settlement Mode', // [CASH-MODE] NEFT/IMPS, not UPI/Cash
      width: 130
    },
    {
      field: 'settlementDate',
      headerName: 'Settlement Date',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return 'N/A';
        return format(new Date(params.value), 'dd MMM yyyy, hh:mm a');
      }
    }
  ];

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />
      <Typography variant="h4" gutterBottom sx={{ mb: 3, mt: 2 }}>
        AceMoney Settlements Report
      </Typography>

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Settlements
                </Typography>
                <Typography variant="h5">{statistics.totalCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Approved
                </Typography>
                <Typography variant="h5" color="success.main">
                  {statistics.approvedCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h5">
                  ₹{statistics.totalAmount?.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Commission
                </Typography>
                <Typography variant="h5">
                  ₹{statistics.totalCommission?.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              {/* [CASH-MODE] renamed: NEFT/IMPS, not UPI/Cash */}
              <InputLabel>Settlement Mode</InputLabel>
              <Select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                label="Settlement Mode"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="NEFT">NEFT</MenuItem>
                <MenuItem value="RTGS">RTGS</MenuItem>
                <MenuItem value="IMPS">IMPS</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchSettlements}
              fullWidth
              disabled={loading}
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Search by Settlement Ref, RRN, Agent Code, Shop Name, Mobile"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              fullWidth
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={handleExport}
              fullWidth
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={settlements}
          columns={columns}
          pageSize={pagination.limit}
          loading={loading}
          rowsPerPageOptions={[25, 50, 100]}
          pagination
          paginationMode="server"
          rowCount={pagination.totalRecords}
          page={pagination.currentPage - 1}
          onPageChange={handlePageChange}
          getRowId={(row) => row._id}
          disableSelectionOnClick
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Settlement Details</DialogTitle>
        <DialogContent>
          {selectedSettlement && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Reference
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.settlementRefNo}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  YPP Reference
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.yppReferenceNumber || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Shop Name
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.shopId?.shopname || selectedSettlement.shopName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Shop Mobile
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.shopId?.phoneNumber || selectedSettlement.shopMobile}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Amount
                </Typography>
                <Typography variant="body1">
                  ₹{selectedSettlement.settlementAmount?.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Commission
                </Typography>
                <Typography variant="body1">
                  ₹{selectedSettlement.commission?.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Registered Count
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.registeredCount}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Not Registered Count
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.notRegisteredCount}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedSettlement.settlementStatus?.toUpperCase()}
                  color={getStatusColor(selectedSettlement.settlementStatus)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* [CASH-MODE] renamed: NEFT/IMPS, not UPI/Cash */}
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Mode
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.paymentMode}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement Date
                </Typography>
                <Typography variant="body1">
                  {format(new Date(selectedSettlement.settlementDate), 'dd MMM yyyy, hh:mm a')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Settlement RRN
                </Typography>
                <Typography variant="body1">
                  {selectedSettlement.settlementRrn || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Message</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsolidatedAcemoneySettlements;
